import React, { useState } from 'react';
import { translate } from '../../../services/translate';
import Toast from 'react-native-toast-message';
import { deacService, VacancyPresenceStatus } from '../../../services/central-api/deac';
import moment from 'moment';
import ExcelJS from 'exceljs';
import CustomButton from '../../../components/CustomButton';
import { DeacParamList } from '../../../typings/Params';

interface ModalProps {
    displayType: VacancyPresenceStatus;
    filters: DeacParamList['PresenceControl'];
    summary: {
        pending: number;
        presences: number;
        absences: number;
    };
    periodMessage: string;
}

export default function PresenceControlReport({ displayType, filters, summary, periodMessage }: ModalProps) {
    const [isLoadingPresencesCsv, setIsLoadingPresencesCsv] = useState<boolean>(false);

    async function getPresencesReport() {
        try {
            setIsLoadingPresencesCsv(true);
            let returnedItens = 0;
            const filtersCopy = { ...filters };
            const summaryCopy = { ...summary };
            filtersCopy.page = 0;
            filtersCopy.limit = 100;

            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet('Presenças DEAC');
            sheet.addRow([translate('resume'), periodMessage]).commit();
            sheet.addRow([translate('pendingParticipation'), translate('presenceSummary'), translate('absenceSummary')]).commit();
            sheet.addRow([summaryCopy.pending, summaryCopy.presences, summaryCopy.absences]).commit();
            sheet.addRow([]).commit();
            sheet.addRow([
                translate('status'),
                translate('type'),
                translate('agentName'),
                'RF',
                translate('role'),
                translate('assignment'),
                translate('vacancyId'),
                translate('vacancyLevel'),
                translate('activityTitle'),
                translate('activityStartDate'),
                translate('activityFinishDate'),
                translate('workplace'),
                translate('workplaceIdentifier'),
                translate('agentActingBody'),
                translate('agentUnit'),
                translate('registeredBy'),
                translate('registeredIn'),
                translate('changedBy'),
                translate('changedIn')
            ]).commit();
            do {
                const response = await deacService.getPresences({
                    ...filtersCopy,
                    type: displayType
                });
                returnedItens = response.rows.length;

                for (const presence of response.rows) {
                    sheet.addRow([
                        presence.present === null ? translate('pending') : presence.present ? translate('confirmed') : translate('absent'),
                        presence.absentJustification === null ? '' : presence.absentJustification ? translate('justifiedAbsence') : translate('unjustifiedAbsence'),
                        (presence.user?.warName && presence.user?.warName != '') ? presence.user?.warName : presence.user?.name ?? '',
                        presence.user?.registry ?? '',
                        presence.user?.role?.name ?? '',
                        presence.assignment?.name ?? '',
                        presence.vacancy?.id ?? '',
                        presence.vacancy?.levels.map(l => l.name).join(', ') ?? '',
                        presence.vacancy?.title ?? '',
                        moment(presence.vacancy.activityStart).format('DD/MM/YYYY HH:mm'),
                        moment(presence.vacancy.activityFinish).format('DD/MM/YYYY HH:mm'),
                        presence.vacancy.workplace.name ?? '',
                        `${presence.vacancy.workplace.actingBodyUnit.actingBody.name} - ${presence.vacancy.workplace.actingBodyCommand.name} - ${presence.vacancy.workplace.actingBodyUnit.name}`,
                        presence.user?.actingBodyUnit?.actingBody?.name ?? '',
                        presence.user?.actingBodyUnit?.name ?? '',
                        presence.presenceRegisteredBy?.name ?? '',
                        presence.presenceRegistration ? moment(presence.presenceRegistration).format('YYYY-MM-DD HH:mm:ss') : '',
                        presence.presenceChangedBy?.name ?? '',
                        presence.presenceAlteration ? moment(presence.presenceAlteration).format('YYYY-MM-DD HH:mm:ss') : '',
                    ]).commit();
                }

                filtersCopy.page += 1;
            } while (returnedItens >= filtersCopy.limit);

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = 'presencas.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(objectUrl);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoadingPresencesCsv(false);
        }
    }

    return (
        <CustomButton
            text={translate('exportCSV')}
            onPress={getPresencesReport}
            disabled={isLoadingPresencesCsv}
        />);
}
